import { ChevronRight } from 'lucide-react';
import type { TransactionItem } from '@/types/transactions';
import { cn } from '@/utils/cn';
import {
  formatTransactionAmount,
  getCategoryTone,
  getTransactionStatusTone,
} from '@/utils/transactionFormatters';

interface TransactionListItemProps {
  transaction: TransactionItem;
  onClick?: () => void;
  showUser?: boolean;
  className?: string;
}

export function TransactionListItem({
  transaction,
  onClick,
  showUser = false,
  className,
}: TransactionListItemProps) {
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 text-left shadow-sm transition hover:border-brand-green/30 hover:shadow-md',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase', getCategoryTone(transaction.category))}>
            {transaction.categoryLabel}
          </span>
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize', getTransactionStatusTone(transaction.status))}>
            {transaction.status}
          </span>
        </div>
        <p className="mt-1 truncate font-semibold text-brand-navy">{transaction.serviceName}</p>
        <p className="truncate text-xs text-gray-500">{transaction.kindLabel}</p>
        {showUser && transaction.user && (
          <p className="mt-1 truncate text-xs text-gray-500">
            {transaction.user.firstName} {transaction.user.lastName} · {transaction.user.phone}
          </p>
        )}
        <p className="mt-1 text-[11px] text-gray-400">
          {new Date(transaction.createdAt).toLocaleString('en-NG', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <p
          className={cn(
            'text-sm font-bold',
            transaction.type === 'credit' ? 'text-emerald-600' : 'text-brand-navy',
          )}
        >
          {formatTransactionAmount(transaction)}
        </p>
        {onClick && <ChevronRight className="h-4 w-4 text-gray-400" />}
      </div>
    </Component>
  );
}
